<template>
    <filter-panel
        :filterOptions="filterOptions"
        :collapsed="true"
        :model-value="filterState"
        @update:modelValue="onUpdateFilters"
    />
    <v-data-table
        :headers="sampleSetHeaders"
        :items="filteredSampleSets"
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
        :search="search"
    >
        <template v-slot:item.actions="{ item }">
            <v-row>
                <v-icon @click="editSampleSet(item)">$edit</v-icon>
                <v-icon @click="createRequestForSampleSet(item)">$plus</v-icon>
            </v-row>
        </template>
        <template v-slot:item.status="{ item }">
            <div v-if="editedItem?.id !== item.id">
                <span @click="startEdit(item)" class="editable-text">{{ item.status }}</span>
            </div>
            <div v-else>
                <v-autocomplete
                    v-model="editedItem.status"
                    :items="sampleSetStatusOptions"
                    density="compact"
                    variant="outlined"
                    label="Edit Status"
                    class="mt-2"
                ></v-autocomplete>
                <v-btn class="mb-1" size="small" variant="text" color="blue" @click="saveEdit(item)"
                    >Save</v-btn
                >
                <v-btn class="mb-1" size="small" variant="text" color="red" @click="cancelEdit"
                    >Cancel</v-btn
                >
            </div>
        </template>
        <template v-slot:item.requestCodes="{ item }">
            <div v-if="item.requestCodes && item.requestCodes.length > 0">
                <span v-for="(code, index) in item.requestCodes" :key="code">
                    {{ code }}<span v-if="index < item.requestCodes.length - 1">, </span>
                </span>
            </div>
        </template>
    </v-data-table>
    <v-dialog v-model="createRequestDialogVisible" max-width="600px">
        <RequestForm
            ref="requestForm"
            class="mt-4"
            :protocols="requestProtocols"
            :embedded="false"
            v-model="request"
            v-model:valid="requestValid"
            @canceled="createRequestDialogVisible = false"
            @saved="handleRequestSaved"
        />
    </v-dialog>
</template>

<script setup lang="ts">
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import {
    Request,
    RequestStatus,
    samplesAPI,
    SavedMaterialSampleSet,
    SavedRequestProtocol,
} from "@/services/samples/samplesAPI";
import { ref, computed, watch } from "vue";
import FilterPanel from "../common/FilterPanel.vue";
import RequestForm from "./RequestForm.vue";

const { sampleSets, search } = defineProps<{
    sampleSets: SavedMaterialSampleSet[];
    search: string;
}>();

const filterState = ref(<Record<string, string[]>>{});
const filterConfig = ref([
    { name: "collectionSOP", prop: "sampleSopName" },
    { name: "status", prop: "status" },
    { name: "sourceExperiment", prop: "experimentCode" },
]);

const filterOptions = ref({});

const request = ref<Request>({
    experimentCode: "",
    status: RequestStatus.DRAFT,
    needByDate: null,
    requestedByEmail: "",
    sourceSampleSet: null,
    protocol: null,
    processor: null,
    requestContainerType: null,
    samplesContainerType: null,
});

const requestValid = ref(false);

const sampleSetRows = computed(() => {
    return sampleSets.map(mapSampleSetToRow);
});

const filteredSampleSets = computed(() => {
    return filterData(
        sampleSetRows.value,
        filterState.value,
        filterConfig.value
    ) as SavedMaterialSampleSet[];
});

const sampleSetStatusOptions = ref(["draft", "canceled", "awaiting collection", "received"]);

const editedItem = ref<SavedMaterialSampleSet | null>(null);

const createRequestDialogVisible = ref(false);

const selectedItem = ref<SavedMaterialSampleSet | null>(null);

const requestProtocols = ref<SavedRequestProtocol[]>([]);

watch(
    () => sampleSets,
    (newSampleSets) => {
        filterOptions.value = buildFilterOptionsFromData(
            newSampleSets.map(mapSampleSetToRow),
            filterConfig.value
        );
    },
    { immediate: true }
);

const emit = defineEmits<{
    (e: "editSampleSet", item: SavedMaterialSampleSet): void;
    (e: "sampleSetUpdated", item: SavedMaterialSampleSet): void;
    (e: "requestCreated", item: SavedMaterialSampleSet): void;
}>();

const sampleSetHeaders = ref([
    { title: "Sample Set ID", value: "code", sortable: true },
    { title: "Collection SOP", value: "sampleSopName", sortable: true },
    { title: "Source Experiment", value: "experimentCode", sortable: true },
    { title: "# of Samples", value: "sampleCount", sortable: true },
    { title: "Status", value: "status", sortable: true },
    { title: "Related Requests", value: "requestCodes", sortable: false },
    { title: "Actions", value: "actions", sortable: false },
]);

function editSampleSet(item: SavedMaterialSampleSet) {
    emit("editSampleSet", item);
}

async function createRequestForSampleSet(item: SavedMaterialSampleSet) {
    if (requestProtocols.value.length === 0) {
        requestProtocols.value = await samplesAPI.fetchRequestProtocols();
    }
    selectedItem.value = item;
    request.value.sourceSampleSet = item;
    request.value.experimentCode = item.experimentCode;
    createRequestDialogVisible.value = true;
}

function onUpdateFilters(newFilterState: Record<string, string[]>) {
    filterState.value = newFilterState;
}

function mapSampleSetToRow(set: SavedMaterialSampleSet) {
    return {
        ...set,
        sampleSopName: set.sampleSop?.name || "No SOP",
    };
}

function startEdit(item: SavedMaterialSampleSet) {
    editedItem.value = { ...item };
}

async function saveEdit(item: SavedMaterialSampleSet) {
    if (editedItem.value) {
        await samplesAPI.updateSampleSet(item.id, {
            status: editedItem.value.status,
        });
        editedItem.value = null;
    }
    emit("sampleSetUpdated", item);
}

function cancelEdit() {
    editedItem.value = null;
}

async function handleRequestSaved() {
    createRequestDialogVisible.value = false;
    emit("requestCreated", selectedItem.value!);
}
</script>

<style scoped>
.editable-text {
    cursor: pointer;
    text-decoration: underline;
}
</style>
